import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidenav, Nav } from 'rsuite';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    FileText,
    FilePlus,
    FileStack,
    Users,
    Settings,
    User,
    Star,
    Sparkles,
    BarChart3,
    TrendingUp,
    ChevronLeft,
    ChevronRight,
    Briefcase,
    DollarSign,
    Wrench,
    Bell,
    UserCog,
    Shield,
    Building2,
    ScrollText,
    Hash,
    CreditCard
} from 'lucide-react';
import { Tooltip } from './ui';
import defaultLogo from '../assets/almaquinta.svg';

const Sidebar = ({ configuracion, expanded, onExpandedChange }) => {
    const { user, tienePermiso } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const empresaNombre = configuracion?.empresa_nombre || 'Alma Industria Creativa';

    // Badge pequeno IA — se renderiza dentro de los Nav.Item de IA
    const IABadge = () => (
        <span className="sidebar-ia-badge" aria-label="Funcionalidad con IA">IA</span>
    );

    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

    const setExpanded = (val) => {
        const newVal = typeof val === 'function' ? val(expanded) : val;
        onExpandedChange(newVal);
    };

    useEffect(() => {
        const handleResize = () => {
            const small = window.innerWidth < 1024;
            setIsSmallScreen(small);
            onExpandedChange(!small);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [onExpandedChange]);

    // Logo URL - usa el logo de configuracion si existe, sino el default
    const getLogoUrl = () => {
        const logo = configuracion?.empresa_logo;
        if (!logo) return defaultLogo;
        if (logo.startsWith('data:') || logo.startsWith('http')) return logo;
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${baseUrl}${logo}`;
    };

    const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';

    // Active key basado en la ruta actual
    const getActiveKey = () => {
        const path = location.pathname;
        if (path === '/dashboard') {
            return location.hash === '#analisis-ia' ? 'ai-analisis' : 'dashboard';
        }
        if (path === '/cotizaciones/generar-ia') return 'ai-generar';
        if (path === '/metricas-ia') return 'metricas-ia';
        if (path === '/cotizaciones/nueva') return 'cot-nueva';
        if (path === '/cotizaciones/nueva/manual') return 'cot-nueva-manual';
        if (path === '/cotizaciones/mis-cotizaciones') return 'cot-mis';
        if (path === '/cotizaciones/clientes') return 'cot-clientes';
        if (path.startsWith('/cotizaciones/cliente/')) return 'cot-clientes';
        if (path === '/cotizaciones') return 'cot-listado';
        if (path.startsWith('/cotizaciones/')) return 'cot-listado';
        if (path.startsWith('/plantillas')) return 'plantillas';
        if (path === '/clientes') return 'clientes';
        if (path === '/alertas') return 'alertas';
        if (path === '/perfil') return 'perfil';
        if (path === '/servicios') return 'servicios';
        if (path === '/servicios/categorias') return 'servicios-categorias';
        if (path === '/servicios/periodos') return 'servicios-periodos';
        if (path === '/costos-fijos') return 'costos-fijos';
        if (path === '/costos-fijos/categorias') return 'cf-categorias';
        if (path === '/costos-fijos/periodos') return 'cf-periodos';
        if (path === '/pagos/planes') return 'pagos-planes';
        if (path === '/personal') return 'personal';
        if (path === '/configuracion') return 'configuracion';
        if (path === '/datos-empresa') return 'datos-empresa';
        if (path === '/configuracion-ia') return 'ia-general';
        if (path === '/configuracion-ia/general')  return 'ia-general';
        if (path === '/configuracion-ia/gemini')   return 'ia-gemini';
        if (path === '/configuracion-ia/deepseek') return 'ia-deepseek';
        if (path === '/configuracion-ia/claude')   return 'ia-claude';
        if (path === '/usuarios') return 'usuarios';
        if (path === '/roles') return 'roles';
        if (path === '/logs') return 'logs';
        if (path === '/numeraciones') return 'numeraciones';
        return 'dashboard';
    };

    // Rutas que viven dentro del dropdown "Ajustes" — se usa para auto-abrirlo
    // cuando el usuario aterriza directo en una de esas paginas.
    const ajustesPaths = ['/configuracion', '/datos-empresa', '/alertas', '/numeraciones'];
    const isAjustesPath = ajustesPaths.some(p => location.pathname === p);
    const isIAPath = location.pathname.startsWith('/configuracion-ia');

    const [openKeys, setOpenKeys] = useState(() => {
        const keys = [];
        if (isAjustesPath) keys.push('ajustes');
        if (isIAPath) keys.push('ia-menu');
        return keys;
    });

    useEffect(() => {
        setOpenKeys(prev => {
            let next = prev;
            if (isAjustesPath && !next.includes('ajustes')) next = [...next, 'ajustes'];
            if (isIAPath && !next.includes('ia-menu')) next = [...next, 'ia-menu'];
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Navegación
    const routeMap = {
        'dashboard': '/dashboard',
        'ai-generar': '/cotizaciones/generar-ia',
        'metricas-ia': '/metricas-ia',
        'cot-nueva': '/cotizaciones/nueva',
        'cot-nueva-manual': '/cotizaciones/nueva/manual',
        'cot-mis': '/cotizaciones/mis-cotizaciones',
        'cot-listado': '/cotizaciones',
        'cot-clientes': '/cotizaciones/clientes',
        'plantillas': '/plantillas',
        'clientes': '/clientes',
        'alertas': '/alertas',
        'perfil': '/perfil',
        'servicios': '/servicios',
        'servicios-categorias': '/servicios/categorias',
        'servicios-periodos': '/servicios/periodos',
        'personal': '/personal',
        'costos-fijos': '/costos-fijos',
        'cf-categorias': '/costos-fijos/categorias',
        'cf-periodos': '/costos-fijos/periodos',
        'pagos-planes': '/pagos/planes',
        'configuracion': '/configuracion',
        'datos-empresa': '/datos-empresa',
        'configuracion-ia': '/configuracion-ia',
        'ia-general':  '/configuracion-ia/general',
        'ia-gemini':   '/configuracion-ia/gemini',
        'ia-deepseek': '/configuracion-ia/deepseek',
        'ia-claude':   '/configuracion-ia/claude',
        'usuarios': '/usuarios',
        'roles': '/roles',
        'logs': '/logs',
        'numeraciones': '/numeraciones',
    };

    const handleSelect = (eventKey) => {
        if (eventKey === 'ai-analisis') {
            navigate('/dashboard#analisis-ia');
            setTimeout(() => {
                const el = document.getElementById('analisis-ia');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 120);
            if (isSmallScreen) setExpanded(false);
            return;
        }
        if (eventKey === 'dashboard') {
            // Forzar navegar a /dashboard sin hash y hacer scroll al top
            navigate('/dashboard', { replace: false });
            if (window.location.hash) {
                window.history.replaceState(null, '', '/dashboard');
            }
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
            if (isSmallScreen) setExpanded(false);
            return;
        }
        if (routeMap[eventKey]) {
            navigate(routeMap[eventKey]);
            if (isSmallScreen) {
                setExpanded(false);
            }
        }
    };

    return (
        <>
            {/* Overlay cuando sidebar expandido en mobile */}
            {isSmallScreen && expanded && (
                <div className="sidebar-overlay" onClick={() => setExpanded(false)} />
            )}

            <aside className={`app-sidebar ${expanded ? '' : 'app-sidebar--collapsed'} ${isSmallScreen ? 'app-sidebar--mobile' : ''}`}>
                {/* Logo + brand */}
                <div className={`flex items-center gap-3 border-b border-line shrink-0 ${expanded ? 'px-5 py-4' : 'justify-center px-1 py-3'}`}>
                    <img
                        src={getLogoUrl()}
                        alt="Logo"
                        className={`rounded-xl bg-white/95 p-1 shrink-0 object-contain ${expanded ? 'w-11 h-11' : 'w-9 h-9'}`}
                    />
                    {expanded && (
                        <div className="min-w-0">
                            <h1 className="text-sm font-semibold text-text truncate leading-tight">Don Presupuesto</h1>
                            <p className="text-[11px] text-muted truncate">{empresaNombre}</p>
                        </div>
                    )}
                </div>

                {/* Navegación + Toggle */}
                <Sidenav
                    expanded={expanded}
                    appearance="subtle"
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    className="sidebar-sidenav"
                >
                    <Sidenav.Body>
                        {expanded && <div className="sidebar-section-label">Inicio</div>}
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            <Nav.Item eventKey="dashboard" icon={<LayoutDashboard size={18} />}>
                                Dashboard
                            </Nav.Item>
                        </Nav>

                        {(tienePermiso('usar_ia') || tienePermiso('ver_metricas_ia')) && expanded && (
                            <div className="sidebar-section-label">Inteligencia Artificial</div>
                        )}
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            {tienePermiso('usar_ia') && (
                                <Nav.Item eventKey="ai-generar" className="ai-flash-item" icon={<Sparkles size={18} className="ai-sidebar-icon" />}>
                                    <span className="sidebar-ia-label">Cotizar con IA<IABadge /></span>
                                </Nav.Item>
                            )}
                            {(tienePermiso('usar_ia') || tienePermiso('ver_metricas_ia')) && (
                                <Nav.Item eventKey="ai-analisis" icon={<BarChart3 size={18} />}>
                                    <span className="sidebar-ia-label">Análisis Dashboard<IABadge /></span>
                                </Nav.Item>
                            )}
                            {tienePermiso('ver_metricas_ia') && (
                                <Nav.Item eventKey="metricas-ia" icon={<TrendingUp size={18} />}>
                                    <span className="sidebar-ia-label">Métricas IA<IABadge /></span>
                                </Nav.Item>
                            )}
                        </Nav>

                        {expanded && <div className="sidebar-section-label">Cotizaciones</div>}
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            {tienePermiso('gestion_cotizaciones') && (
                                <Nav.Menu
                                    eventKey="cot-nueva-menu"
                                    title="Nueva Cotización"
                                    icon={<FilePlus size={18} />}
                                    className={(location.pathname === '/cotizaciones/nueva' || location.pathname === '/cotizaciones/nueva/manual') ? 'is-active' : ''}
                                >
                                    <Nav.Item eventKey="cot-nueva">
                                        <span className="sidebar-tipo-item">
                                            <strong>Normal</strong>
                                            <span className="sidebar-tipo-hint">Cotizaciones habituales</span>
                                        </span>
                                    </Nav.Item>
                                    <Nav.Item eventKey="cot-nueva-manual">
                                        <span className="sidebar-tipo-item">
                                            <strong>Histórico</strong>
                                            <span className="sidebar-tipo-hint">Cotizaciones antiguas</span>
                                        </span>
                                    </Nav.Item>
                                </Nav.Menu>
                            )}
                            <Nav.Item eventKey="cot-mis" icon={<Star size={18} style={{ fill: '#eab308', stroke: '#eab308' }} />}>
                                Mis Cotizaciones
                            </Nav.Item>
                            {tienePermiso('gestion_cotizaciones') && (
                                <Nav.Item eventKey="cot-listado" icon={<FileText size={18} />}>
                                    Listado
                                </Nav.Item>
                            )}
                            {tienePermiso('gestion_cotizaciones') && (
                                <Nav.Item eventKey="cot-clientes" icon={<Users size={18} />}>
                                    Por Cliente
                                </Nav.Item>
                            )}
                            {tienePermiso('gestion_plantillas') && (
                                <Nav.Item eventKey="plantillas" icon={<FileStack size={18} />}>
                                    Plantillas
                                </Nav.Item>
                            )}
                        </Nav>

                        {tienePermiso('gestion_clientes') && expanded && (
                            <div className="sidebar-section-label">Clientes</div>
                        )}
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            {tienePermiso('gestion_clientes') && (
                                <Nav.Item eventKey="clientes" icon={<Users size={18} />}>
                                    Clientes
                                </Nav.Item>
                            )}
                        </Nav>
                        {(tienePermiso('gestion_servicios') || tienePermiso('gestion_personal') || tienePermiso('gestion_costos_fijos') || tienePermiso('gestion_planes_pago') || tienePermiso('configuracion') || tienePermiso('configuracion_alertas') || tienePermiso('gestion_usuarios') || tienePermiso('gestion_roles') || tienePermiso('ver_logs_sistema')) && expanded && (
                            <div className="sidebar-section-label">Administración</div>
                        )}
                        <Nav activeKey={getActiveKey()} onSelect={handleSelect}>
                            {/* Catalogos planos arriba — uso frecuente en el dia a dia */}
                            {tienePermiso('gestion_servicios') && (
                                <Nav.Menu
                                    eventKey="servicios-menu"
                                    title="Servicios"
                                    icon={<Briefcase size={18} />}
                                    className={(location.pathname === '/servicios' || location.pathname === '/servicios/categorias' || location.pathname === '/servicios/periodos') ? 'is-active' : ''}
                                >
                                    <Nav.Item eventKey="servicios">Servicios</Nav.Item>
                                    <Nav.Item eventKey="servicios-categorias">Categorías</Nav.Item>
                                    <Nav.Item eventKey="servicios-periodos">Períodos</Nav.Item>
                                </Nav.Menu>
                            )}
                            {tienePermiso('gestion_personal') && <Nav.Item eventKey="personal" icon={<Users size={18} />}>Personal</Nav.Item>}
                            {tienePermiso('gestion_costos_fijos') && (
                                <Nav.Menu
                                    eventKey="costos-fijos-menu"
                                    title="Costos Fijos"
                                    icon={<DollarSign size={18} />}
                                    className={(location.pathname === '/costos-fijos' || location.pathname === '/costos-fijos/categorias' || location.pathname === '/costos-fijos/periodos') ? 'is-active' : ''}
                                >
                                    <Nav.Item eventKey="costos-fijos">Costos Fijos</Nav.Item>
                                    <Nav.Item eventKey="cf-categorias">Categorías</Nav.Item>
                                    <Nav.Item eventKey="cf-periodos">Períodos</Nav.Item>
                                </Nav.Menu>
                            )}
                            {tienePermiso('gestion_planes_pago') && (
                                <Nav.Menu
                                    eventKey="pagos-menu"
                                    title="Pagos"
                                    icon={<CreditCard size={18} />}
                                    className={location.pathname.startsWith('/pagos') ? 'is-active' : ''}
                                >
                                    <Nav.Item eventKey="pagos-planes">Planes de pago</Nav.Item>
                                </Nav.Menu>
                            )}

                            {/* Dropdown "Ajustes" — config que se toca una vez y rara vez.
                                className="is-active" cuando estamos en una ruta hija, para que
                                el padre se vea resaltado (rsuite no lo hace solo). */}
                            {(tienePermiso('configuracion') || tienePermiso('configuracion_alertas')) && (
                                <Nav.Menu
                                    eventKey="ajustes"
                                    title="Ajustes"
                                    icon={<Settings size={18} />}
                                    className={isAjustesPath ? 'is-active' : ''}
                                >
                                    {tienePermiso('configuracion') && <Nav.Item eventKey="configuracion" icon={<Wrench size={16} />}>Configuración</Nav.Item>}
                                    {tienePermiso('configuracion') && <Nav.Item eventKey="datos-empresa" icon={<Building2 size={16} />}>Datos de empresa</Nav.Item>}
                                    {tienePermiso('configuracion') && <Nav.Item eventKey="numeraciones" icon={<Hash size={16} />}>Numeraciones</Nav.Item>}
                                    {tienePermiso('configuracion_alertas') && <Nav.Item eventKey="alertas" icon={<Bell size={16} />}>Alertas</Nav.Item>}
                                </Nav.Menu>
                            )}

                            {/* Proveedores IA — menu propio (rsuite no permite Nav.Menu anidados,
                                asi que va hermano de "Ajustes" en vez de dentro de el). */}
                            {tienePermiso('configuracion') && (
                                <Nav.Menu
                                    eventKey="ia-menu"
                                    title={<span className="sidebar-ia-label">Proveedores IA<IABadge /></span>}
                                    icon={<Sparkles size={18} />}
                                    className={isIAPath ? 'is-active' : ''}
                                >
                                    <Nav.Item eventKey="ia-general">General</Nav.Item>
                                    <Nav.Item eventKey="ia-gemini">Gemini</Nav.Item>
                                    <Nav.Item eventKey="ia-deepseek">DeepSeek</Nav.Item>
                                    <Nav.Item eventKey="ia-claude">Claude</Nav.Item>
                                </Nav.Menu>
                            )}

                            {/* Acceso y seguridad planos abajo — accion admin importante */}
                            {tienePermiso('gestion_usuarios') && <Nav.Item eventKey="usuarios" icon={<UserCog size={18} />}>Usuarios</Nav.Item>}
                            {tienePermiso('gestion_roles') && <Nav.Item eventKey="roles" icon={<Shield size={18} />}>Roles y Permisos</Nav.Item>}
                            {tienePermiso('ver_logs_sistema') && <Nav.Item eventKey="logs" icon={<ScrollText size={18} />}>Logs del sistema</Nav.Item>}
                        </Nav>
                    </Sidenav.Body>
                </Sidenav>

                {/* Footer unificado: perfil + toggle collapse, separados por una sola linea divisora */}
                <div className={`shrink-0 border-t border-line bg-surface ${expanded ? 'flex items-center gap-1 p-2' : 'p-2 flex flex-col items-center gap-1'}`}>
                    <Tooltip content={expanded ? null : 'Mi Perfil'} side="right">
                        <button
                            type="button"
                            onClick={() => { navigate('/perfil'); if (isSmallScreen) setExpanded(false); }}
                            aria-label="Mi Perfil"
                            className={`
                                flex items-center gap-2.5 rounded-lg
                                text-sm font-medium text-muted
                                hover:bg-surface-2 hover:text-text
                                transition
                                ${expanded ? 'flex-1 min-w-0 px-3 py-2' : 'justify-center w-10 h-10 p-0'}
                            `}
                        >
                            <User size={16} className="text-muted shrink-0" />
                            {expanded && (
                                <span className="truncate text-text">{user?.nombre}</span>
                            )}
                        </button>
                    </Tooltip>
                    <Tooltip content={expanded ? 'Colapsar menu' : 'Expandir menu'} side="right">
                        <button
                            type="button"
                            onClick={() => setExpanded(v => !v)}
                            aria-label={expanded ? 'Colapsar menu' : 'Expandir menu'}
                            className="
                                flex items-center justify-center w-9 h-9 rounded-lg
                                text-muted hover:text-text hover:bg-surface-2
                                transition shrink-0
                            "
                        >
                            {expanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                        </button>
                    </Tooltip>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
